import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Archive,
  Braces,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  FileInput,
  FileSpreadsheet,
  Import,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { BulkBar, RowBox, SelectAllBox, useTableSelection } from "../../components/TableSelection";
import { Button, Pagination, Select, StatCard } from "../../components/ui";
import { ImportStatusBadge } from "./ImportData";
import { apiDelete, useApi } from "../../api/client";
import { ImportRecord } from "../../data/types";

/*
 * Import History doubles as the undo for an upload.
 *
 * A workbook that turns out to be wrong is fixed in the spreadsheet and sent
 * again — nobody edits individual strings by hand. Removing an upload takes out
 * everything it wrote, so the corrected file can be uploaded onto clean ground.
 * Competitors are never removed: a SID is a permanent registration, not a
 * result.
 */
export default function ImportHistoryPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data: allImports, loading, reload } = useApi<ImportRecord[]>("/imports", []);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  // One code path for one upload or many — a single removal is a list of one.
  const [pending, setPending] = useState<ImportRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);

  const q = search.trim().toLowerCase();
  const importHistory = allImports.filter(
    (r) =>
      (!q || `${r.fileName} ${r.dataType} ${r.competition} ${r.id}`.toLowerCase().includes(q)) &&
      (typeFilter === "All Types" || r.dataType === typeFilter) &&
      (statusFilter === "All Statuses" || r.status === statusFilter)
  );

  /*
   * Only uploads that can actually be undone are selectable — an upload made
   * before records carried their import id has nothing to trace, so offering it
   * for selection would promise something the removal cannot deliver.
   */
  const selection = useTableSelection(importHistory.filter((r) => r.reversible !== false).map((r) => r.id));

  const removeImports = async () => {
    setBusy(true);
    const messages: string[] = [];
    const failed: string[] = [];
    for (const r of pending) {
      try {
        const res = await apiDelete<{ message: string }>(`/admin/imports/${encodeURIComponent(r.id)}`);
        messages.push(res.message);
      } catch (e) {
        failed.push(`${r.fileName}: ${(e as Error).message}`);
      }
    }
    setBusy(false);
    setPending([]);
    selection.clear();
    reload();
    setNotice(
      failed.length
        ? { tone: "bad", text: `${messages.length} of ${pending.length} removed. ${failed[0]}` }
        : { tone: "ok", text: pending.length === 1 ? messages[0] : `${pending.length} uploads removed. You can now upload corrected files.` }
    );
  };

  /* The full warning list, saved as a plain text file for checking offline. */
  const downloadReport = (r: ImportRecord) => {
    const lines = [
      `Import report — ${r.fileName}`,
      `Import ID: ${r.id}`,
      `Event: ${r.competition}`,
      `Imported by ${r.importedBy} on ${r.date} at ${r.time}`,
      `Status: ${r.status} · ${r.records} shots`,
      "",
      ...(r.errors?.all?.length ? r.errors.all.map((e, i) => `${i + 1}. ${e}`) : ["No messages recorded."]),
    ];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/plain" }));
    a.download = `${r.id}-report.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <AdminTopbar
        title="Import History"
        subtitle="Every upload, what it contained, and the option to remove one so a corrected file can be uploaded."
        actions={<Link to="/admin/import"><Button><Import size={16} /> Import Data</Button></Link>}
      />

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-3 text-[13px] ${
            notice.tone === "ok" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          {notice.tone === "ok" ? <CheckCircle2 size={16} className="mt-px shrink-0" /> : <AlertCircle size={16} className="mt-px shrink-0" />}
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={<FileInput size={20} />} value={allImports.length} label="Total Imports" sub="All Time" />
        <StatCard icon={<CheckCircle2 size={20} />} value={allImports.filter((r) => r.status === "Completed").length} label="Completed" subTone="success" iconBg="bg-success-soft text-success" />
        <StatCard icon={<AlertCircle size={20} />} value={allImports.filter((r) => r.status === "Failed").length} label="Failed" iconBg="bg-warning-soft text-warning" />
        <StatCard icon={<Clock3 size={20} />} value={allImports.filter((r) => r.status === "In Progress").length} label="In Progress" iconBg="bg-violet-50 text-violet-600" />
        <StatCard icon={<Archive size={20} />} value={allImports.reduce((a, r) => a + (r.records || 0), 0).toLocaleString()} label="Shots Imported" sub="All Time" />
      </div>

      <div className="card mt-3 flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-[240px] flex-1">
          <label className="label">Search</label>
          <input
            className="input"
            placeholder="Search by file name, data type or competition..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Data Type</label>
          <Select options={["All Types", "Shoot Workbook", "Results", "Shooters", "Matches", "Classes / Grades"]} value={typeFilter} onChange={setTypeFilter} className="w-full sm:w-44" />
        </div>
        <div>
          <label className="label">Status</label>
          <Select options={["All Statuses", "Completed", "Failed", "In Progress"]} value={statusFilter} onChange={setStatusFilter} className="w-40" />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setSearch("");
            setTypeFilter("All Types");
            setStatusFilter("All Statuses");
          }}
        >
          Reset
        </Button>
      </div>

      <BulkBar
        count={selection.count}
        noun="upload"
        onClear={selection.clear}
        onDelete={() => setPending(importHistory.filter((r) => selection.isSelected(r.id)))}
      />

      <div className="card mt-3">
        <div className="max-h-[calc(100vh-430px)] min-h-[220px] overflow-auto">
          <table className="w-full min-w-[1020px]">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-line bg-slate-50">
                <th className="th w-10">
                  <SelectAllBox
                    checked={selection.allSelected}
                    indeterminate={selection.someSelected}
                    onChange={selection.toggleAll}
                    label="Select all uploads"
                  />
                </th>
                <th className="th">Import ID</th>
                <th className="th">File Name</th>
                <th className="th">Event</th>
                <th className="th text-right">Shots</th>
                <th className="th">Imported By</th>
                <th className="th">Date &amp; Time</th>
                <th className="th">Status</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading && (
                <tr>
                  <td colSpan={9} className="td py-10 text-center text-muted">
                    Loading imports...
                  </td>
                </tr>
              )}
              {!loading && !importHistory.length && (
                <tr>
                  <td colSpan={9} className="td py-10 text-center text-muted">
                    No imports yet — import a shoot workbook to populate the portal.
                  </td>
                </tr>
              )}
              {importHistory.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    className={`cursor-pointer transition-colors ${
                      selection.isSelected(r.id) ? "bg-primary-soft/50" : expanded === r.id ? "bg-primary-soft/40" : "hover:bg-slate-50"
                    }`}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      {r.reversible === false ? (
                        <span title="This upload cannot be removed" className="block h-4 w-4" />
                      ) : (
                        <RowBox checked={selection.isSelected(r.id)} onChange={() => selection.toggle(r.id)} label={`Select ${r.fileName}`} />
                      )}
                    </td>
                    <td className="td font-semibold text-muted">
                      <span className="flex items-center gap-1.5">
                        <ChevronDown size={13} className={`transition-transform ${expanded === r.id ? "" : "-rotate-90"}`} />
                        {r.id}
                      </span>
                    </td>
                    <td className="td">
                      <span className="flex items-center gap-2.5 font-semibold">
                        {r.fileType === "json" ? <Braces size={16} className="text-primary" /> : <FileSpreadsheet size={16} className="text-success" />}
                        {r.fileName}
                      </span>
                    </td>
                    <td className="td text-slate-600">{r.competition}</td>
                    <td className="td text-right font-semibold">{r.records.toLocaleString()}</td>
                    <td className="td text-muted">{r.importedBy}</td>
                    <td className="td text-muted">{r.date}<br />{r.time}</td>
                    <td className="td"><ImportStatusBadge status={r.status} /></td>
                    <td className="td text-right">
                      <span className="inline-flex gap-1.5">
                        <button
                          className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50"
                          aria-label="Download report"
                          title="Download report"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadReport(r);
                          }}
                        >
                          <Download size={14} />
                        </button>
                        <button
                          className="rounded-lg border border-danger/40 p-2 text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Remove upload"
                          title={
                            r.reversible === false
                              ? "This upload was made before uploads could be traced to their records, so it cannot be removed"
                              : "Remove this upload and everything it added"
                          }
                          disabled={r.reversible === false}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPending([r]);
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={9} className="bg-slate-50/70 px-5 pb-5 pt-2">
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                          <div className="rounded-xl border border-line bg-white p-3.5">
                            <h4 className="text-sm font-bold text-ink">This upload</h4>
                            <dl className="mt-2 space-y-1 text-[13px] text-slate-600">
                              <div className="flex justify-between gap-3"><dt>Shots</dt><dd className="font-semibold text-ink">{r.records.toLocaleString()}</dd></div>
                              <div className="flex justify-between gap-3"><dt>File</dt><dd className="truncate font-semibold text-ink">{r.fileName}</dd></div>
                              <div className="flex justify-between gap-3"><dt>Messages</dt><dd className="font-semibold text-ink">{r.errors?.total ?? 0}</dd></div>
                            </dl>
                            <button
                              className="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-danger/40 px-3.5 text-[13px] font-semibold text-danger transition-colors hover:bg-danger-soft disabled:opacity-40"
                              disabled={r.reversible === false}
                              onClick={() => setPending([r])}
                            >
                              <Trash2 size={13} /> Remove this upload
                            </button>
                          </div>
                          <div className="rounded-xl border border-line bg-white p-3.5 lg:col-span-2">
                            <h4 className="flex items-center gap-2 text-sm font-bold text-ink">
                              <TriangleAlert size={15} className="text-warning" /> Import messages
                            </h4>
                            {r.errors?.all?.length ? (
                              <ol className="slim-scrollbar mt-2 max-h-52 space-y-1.5 overflow-y-auto pr-2 text-[13px] leading-relaxed text-slate-600">
                                {r.errors.all.map((msg, i) => (
                                  <li key={i} className="flex gap-2">
                                    <span className="shrink-0 font-semibold text-muted">{i + 1}.</span>
                                    <span className="min-w-0">{msg}</span>
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              <p className="mt-2 text-[13px] text-muted">No messages — everything in this file imported cleanly.</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-line">
          <Pagination summary={`${importHistory.length} of ${allImports.length} imports`} />
        </div>
      </div>

      <ConfirmDialog
        open={pending.length > 0}
        busy={busy}
        title={pending.length === 1 ? "Remove this upload?" : `Remove ${pending.length} uploads?`}
        intro={
          pending.length === 1
            ? "This removes everything that upload added — its shot strings, and any match, day or competition left with nothing in it."
            : "This removes everything these uploads added — their shot strings, and any match, day or competition left with nothing in it."
        }
        items={pending.map((r) => `${r.fileName} — ${r.records.toLocaleString()} shots (${r.id})`)}
        note={
          <>
            Anything another upload contributed stays, and <b>competitors stay on the register</b>. Scores and rankings are
            recalculated from what remains. Correct the spreadsheet and upload it again — there is no need to edit records by
            hand.
          </>
        }
        confirmLabel={pending.length === 1 ? "Remove upload" : `Remove ${pending.length} uploads`}
        onConfirm={removeImports}
        onCancel={() => setPending([])}
      />
    </div>
  );
}
